import { useState, useCallback, useEffect, useRef } from 'react';
import { getInviteCode } from '@/services/familyService';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function InviteModal({ isOpen, onClose }: InviteModalProps) {
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [isInviteLoading, setIsInviteLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const basePath = __BASE_PATH__.replace(/\/+$/, '');
  const signupPath = `${basePath}/signup`;
  const inviteUrl = typeof window !== 'undefined'
    ? (() => {
      const url = new URL(signupPath, window.location.origin);
      if (inviteCode) url.searchParams.set('invite', inviteCode);
      return url.toString();
    })()
    : '/signup';

  const inviteMessage = '우리 가족 앨범에 초대합니다! 소중한 가족 추억을 함께 나눠요 💛';

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // NAS에 설정된 현재 초대코드를 읽어, 링크 안에 함께 넣습니다.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const loadInviteCode = async () => {
      setIsInviteLoading(true);
      setShareError('');
      try {
        const code = await getInviteCode();
        if (!/^\d{6}$/.test(code)) {
          throw new Error('초대코드 형식이 올바르지 않습니다.');
        }
        if (!cancelled) setInviteCode(code);
      } catch (error) {
        if (!cancelled) {
          setInviteCode('');
          setShareError(error instanceof Error ? error.message : '초대 링크를 만들지 못했습니다.');
        }
      } finally {
        if (!cancelled) setIsInviteLoading(false);
      }
    };

    void loadInviteCode();
    return () => { cancelled = true; };
  }, [isOpen]);

  // Lock background scroll & save/restore focus
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        modalRef.current?.focus();
      }, 50);
    } else {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Clear copied state after delay
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleKakaoShare = useCallback(async () => {
    setShareError('');

    // Try Web Share API first (works natively on mobile with KakaoTalk as share target)
    if (navigator.share) {
      try {
        await navigator.share({
          title: '우리 가족 앨범 초대',
          text: inviteMessage,
          url: inviteUrl,
        });
        return;
      } catch (err) {
        // User cancelled or API failed - fall through to copy
        if (err instanceof Error && err.name === 'AbortError') {
          return; // User just cancelled
        }
      }
    }

    // Fallback for desktop: open KakaoTalk share if possible, otherwise copy link
    const kakaoShareUrl = `https://accounts.kakao.com/login/?continue=https%3A%2F%2Fsharer.kakao.com%2Ftalk%2Ffriends%2Fpicker%3Ftitle%3D${encodeURIComponent('우리 가족 앨범 초대')}%26description%3D${encodeURIComponent(inviteMessage)}%26linkUrl%3D${encodeURIComponent(inviteUrl)}`;
    
    const newWindow = window.open(kakaoShareUrl, '_blank', 'width=500,height=600');
    if (!newWindow) {
      setShareError('팝업이 차단되었습니다. 팝업 차단을 해제하거나 초대 링크를 복사해주세요.');
      return;
    }

    // Also copy link for convenience
    try {
      await navigator.clipboard.writeText(`${inviteMessage}\n${inviteUrl}`);
    } catch {
      // clipboard might not be available
    }
  }, [inviteUrl, inviteMessage]);

  const handleCopyLink = useCallback(async () => {
    setShareError('');
    try {
      await navigator.clipboard.writeText(`${inviteMessage}\n${inviteUrl}`);
      setCopied(true);
    } catch {
      // Fallback for older browsers / non-HTTPS
      try {
        const textarea = document.createElement('textarea');
        textarea.value = `${inviteMessage}\n${inviteUrl}`;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopied(true);
      } catch {
        setShareError('클립보드 접근이 불가능합니다. 아래 링크를 직접 복사해주세요.');
      }
    }
  }, [inviteUrl, inviteMessage]);

  // Close on backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="가족 초대하기"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 animate-fade-in"
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        tabIndex={-1}
        className="relative w-full sm:max-w-md bg-background-50 rounded-t-3xl sm:rounded-3xl animate-slide-up sm:animate-scale-in max-h-[85vh] overflow-y-auto"
      >
        {/* Handle bar for mobile */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-foreground-200" />
        </div>

        {/* Header */}
        <div className="px-6 pt-4 sm:pt-6 pb-2 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-heading font-bold text-foreground-950">
              가족 초대하기
            </h2>
            <p className="text-sm text-foreground-500 mt-1">
              소중한 가족과 추억을 함께 나눠보세요
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-secondary-100 transition-colors cursor-pointer flex-shrink-0"
            aria-label="닫기"
          >
            <i className="ri-close-line text-lg text-foreground-500" />
          </button>
        </div>

        {/* Invite illustration area */}
        <div className="px-6 py-5">
          <div className="bg-secondary-50 rounded-2xl p-5 text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
              <i className="ri-heart-add-line text-2xl text-accent-500" />
            </div>
            <p className="text-sm text-foreground-700 leading-relaxed">
              초대 링크를 공유하면<br />
              가족 구성원이{' '}
              <strong className="text-accent-600 font-semibold">회원가입</strong>
              할 수 있어요.<br />
              관리자 승인 후 앨범을 함께 볼 수 있습니다.
            </p>
          </div>
        </div>

        {/* Share buttons */}
        <div className="px-6 pb-2 space-y-3">
          {/* KakaoTalk Share Button */}
          <button
            onClick={handleKakaoShare}
            disabled={isInviteLoading || !inviteCode}
            className="w-full flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-xl font-semibold text-sm whitespace-nowrap transition-all cursor-pointer bg-[#FEE500] text-[#191919] hover:bg-[#FDD835] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-kakao-talk-fill text-lg" />
            </div>
            카카오톡으로 공유하기
          </button>

          {/* Copy Link Button */}
          <button
            onClick={handleCopyLink}
            disabled={isInviteLoading || !inviteCode}
            className="w-full flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-xl font-semibold text-sm whitespace-nowrap transition-all cursor-pointer bg-secondary-100 text-foreground-800 hover:bg-secondary-200 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${copied ? 'ri-check-line' : 'ri-link'} text-base`} />
            </div>
            {copied ? '복사 완료!' : '초대 링크 복사하기'}
          </button>
        </div>

        {/* Error message */}
        {shareError && (
          <div className="px-6 pb-3">
            <div className="bg-accent-50 border border-accent-200 rounded-xl px-4 py-3">
              <p className="text-xs text-accent-700 flex items-start gap-2">
                <i className="ri-error-warning-line flex-shrink-0 mt-0.5" />
                <span>{shareError}</span>
              </p>
            </div>
          </div>
        )}

        {/* Direct link display (fallback for when clipboard is unavailable) */}
        <div className="px-6 pb-5 pt-1">
          <p className="text-[11px] text-foreground-400 text-center">
            초대코드가 링크에 포함되어 회원가입 화면에 자동 입력됩니다
          </p>
          <div className="mt-2 bg-background-100 rounded-xl px-4 py-3">
            <p className="text-xs text-foreground-600 break-all select-all leading-relaxed">
              {isInviteLoading
                ? '초대 링크를 준비하는 중입니다...'
                : inviteCode
                  ? inviteUrl
                  : '초대 링크를 만들지 못했습니다. 잠시 후 다시 시도해주세요.'}
            </p>
          </div>
        </div>

        {/* Safe area for mobile */}
        <div className="pb-safe" />
      </div>

      {/* Custom animation styles */}
      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes scale-in {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-scale-in { animation: scale-in 0.25s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-fade-in { animation: fade-in 0.2s ease-out; }
      `}</style>
    </div>
  );
}
